import React, { useEffect, useRef, useState } from 'react';
import utils from '../../utils';
import productServices from '../../services/ProductsServices';
import toast from 'react-hot-toast';

function ProductMedia({ action, activeProduct, addedProduct, updateUI }) {
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [isAdditional, setIsAdditional] = useState(false); // 👈 NEW: track is_additional
    const fileInputRef = useRef(null);

    useEffect(() => {
        setFiles([]);
        setPreviews([]);
        setSelectedIds([]);
        setIsAdditional(false); // reset when switching products
        if (action === 1 && activeProduct) {
            setPreviews(activeProduct?.images);
        }
    }, [action, activeProduct, addedProduct]);


    const handleFiles = (newFiles) => {
        const fileArray = Array.from(newFiles);
        setFiles(prev => [...prev, ...fileArray]);

        fileArray.forEach(file => {
            if (file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                setPreviews(prev => [...prev, { file, url, isNew: true }]);
            } else {
                setPreviews(prev => [...prev, { file, url: null, isNew: true, name: file.name }]);
            }
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };

    const onClickUpload = async () => {
        if (files.length === 0) return;
        setUploading(true);

        const formData = new FormData();
        files.forEach(file => {
            formData.append('media', file);
        });
        // 👇 Append is_additional as string ('true' or 'false')
        formData.append('is_additional', isAdditional ? 'true' : 'false');

        let id = action === 0 ? addedProduct?.id : activeProduct?.id;
        productServices.addProductMedia(id, formData).then(
            res => {
                setUploading(false);
                toast.success("Media files uploaded successfully");
                clearData();
                updateUI();
            },
            err => {
                console.log(err);
                toast.error(utils.toastError);
                setUploading(false);
            }
        );
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        const loadingToast = toast.loading("Deleting media files...");
        const data = JSON.stringify({ ids: selectedIds });
        productServices.deleteProductMedia(activeProduct?.id, data).then(
            res => {
                console.log(res.data);
                toast.success("Successfully deleted product media", {
                    id: loadingToast
                });
                clearData();
                updateUI();
            },
            err => {
                console.log(err);
                toast.error(utils.toastError, {
                    id: loadingToast
                });
            }
        );
    };

    function clearData() {
        setFiles([]);
        setPreviews([]);
        setIsAdditional(false); // reset flag
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Upload Zone */}
                <div
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-secondary transition cursor-pointer"
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <p className="text-gray-600 dark:text-gray-400">
                        Drag & drop files here, or click to select
                    </p>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        onChange={(e) => handleFiles(e.target.files)}
                        className="hidden"
                        accept="image/*,video/*"
                    />
                </div>

                {/* is_additional Toggle */}
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="isAdditional"
                        checked={isAdditional}
                        onChange={(e) => setIsAdditional(e.target.checked)}
                        className="w-4 h-4"
                    />
                    <label htmlFor="isAdditional" className="text-sm text-gray-700 dark:text-gray-300">
                        Mark as additional media
                    </label>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        onClick={onClickUpload}
                        disabled={files.length === 0 || uploading}
                        className={`px-4 py-2 rounded font-medium ${files.length === 0 || uploading
                                ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                                : 'bg-secondary text-white hover:bg-opacity-90'
                            }`}
                    >
                        {uploading ? 'Uploading...' : `Upload (${files.length})`}
                    </button>

                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="px-4 py-2 bg-red-500 text-white rounded font-medium hover:bg-red-600"
                        >
                            Delete ({selectedIds.length})
                        </button>
                    )}
                </div>

                {/* Preview Grid */}
                {previews.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {previews.slice().reverse().map((item, i) => (
                            <div key={item.id || `new-${i}`} className="group relative">
                                {/* Checkbox */}
                                <input
                                    type="checkbox"
                                    checked={selectedIds.includes(item.id)}
                                    onChange={() => item.id && toggleSelect(item.id)}
                                    className="absolute top-2 right-2 z-10 w-5 h-5 rounded border-gray-400 bg-white dark:bg-slate-800"
                                    disabled={!item.id} // can't delete unsaved items
                                />

                                {/* Thumbnail */}
                                {item.url ? (
                                    <img
                                        src={item?.isNew ? `${item.url}` : `${utils.baseUrl}${item.url}`}
                                        alt=""
                                        className="w-full aspect-square object-cover rounded border border-gray-200 dark:border-gray-700"
                                    />
                                ) : (
                                    <div className="w-full aspect-square bg-gray-100 dark:bg-slate-800 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500">
                                        {item.name?.slice(0, 12)}...
                                    </div>
                                )}

                                {item.isNew && (
                                    <span className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded">
                                        New
                                    </span>
                                )}
                                {item.is_additional && (
                                    <span className="absolute top-1 left-1 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded">
                                        Additional
                                    </span>
                                )}

                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProductMedia;